import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import { h2, paragraphLarge } from '@helpers/typography';
import SVGRoundedCorner from '@img/icons/rounded-corner.svg';
import { LinkButton } from '@stories/Components/Buttons/Button/Button';
import PaginationStyles from '@stories/Components/Misc/Carousels/Carousel/Pagination/Pagination.styles';
import GenericContentCarouselStyles from '@stories/Components/Misc/Carousels/FullWidthCarousel/FullWidthCarousel.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import styled from 'styled-components';
const HeroHomepage = styled.div `
  min-height: calc(80vh - var(--headerHeight, 0px));
  min-height: calc(80dvh - var(--headerHeight, 0px));

  @media ${from(Device.TabletSmall)} {
    min-height: 652px;
  }

  ${GenericContentCarouselStyles.CarouselWrapper} {
    --itemPeek: 0px;
    --itemGutter: 0;
    --visibleItems: 1;

    margin: 0;

    @media ${from(Device.Desktop)} {
      grid-row: 1;
      grid-column: 1;
    }
  }

  > ${SectionWrapperStyles.Container} {
    > ${SectionWrapperStyles.Inner} {
      position: relative;
      max-width: none;
      padding: 0;
      display: block;
    }
  }

  ${PaginationStyles.Container} {
    width: 100%;
    margin: 0;
    height: 52px;
    grid-row: 2;
    grid-column: 1;
    justify-content: center;
    background-color: ${brand.global.white};
    z-index: ${Layers.Slider};

    @media ${from(Device.Desktop)} {
      justify-content: center;
      margin: 0;
    }
  }
`;
const Slide = styled.div `
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: 100%;
  max-width: 1920px;
  margin: 0 auto;

  @media ${from(Device.TabletSmall)} {
    align-items: center;
    min-height: 652px;
  }

  @media ${until(Device.TabletSmall)} {
    &[data-layout='wide'],
    &[data-layout='split'] {
      align-items: flex-start;
    }
  }
`;
const Inner = styled(SectionWrapper) `
  width: 100%;

  @media ${until(Device.TabletSmall)} {
    --sectionSpacingBottom: 0;

    ${Slide}[data-layout='wide'] & {
      --sectionSpacingTop: 0;
    }

    ${Slide}[data-layout='split'] & {
      --sectionSpacingTop: 24px;
    }
  }

  ${SectionWrapperStyles.Inner} {
    width: 100%;
  }
`;
const Content = styled.div `
  position: relative;
  width: 100%;
  padding: ${fluid(20, 36)};
  border-radius: ${fluid(24, 48)} ${fluid(24, 48)} 0 0;
  background-color: ${brand.global.white};
  color: ${brand.primary.blue};
  z-index: 1;

  ${Slide}[data-layout='split'] & {
    padding: ${fluid(43, 53)} ${fluid(32, 68)} ${fluid(38, 48)};
  }

  @media ${from(Device.TabletSmall)} {
    max-width: 540px;
    border-radius: ${fluid(24, 48)};

    ${Slide}[data-layout='wide'] & {
      max-width: 716px;
    }

    ${Slide}[data-layout='split'] & {
      max-width: 50%;
    }

    ${Slide}[data-layout='full-width'] & {
      max-width: 564px;
    }

    ${Slide}[data-layout='slim'] &,
    ${Slide}[data-layout='wide'] & {
      padding-left: 0;
    }
  }

  @media ${until(Device.TabletSmall)} {
    ${Slide}[data-layout='wide'] & {
      border-radius: 0 0 ${fluid(24, 48)} ${fluid(24, 48)};
    }

    ${Slide}[data-layout='full-width'] & {
      border-radius: 24px;
      margin-bottom: 16px;
    }
  }
`;
const Title = styled.h2 `
  ${h2};
`;
const Subtitle = styled.p `
  ${paragraphLarge};

  margin-bottom: 32px;
`;
const Cta = styled(LinkButton) `
  @media ${until(Device.TabletSmall)} {
    width: 100%;
    justify-content: center;
  }
`;
const RoundedCorner = styled(SVGRoundedCorner) `
  position: absolute;
  width: 68px;
  height: 68px;

  &[data-position='top-left'] {
    top: 0;
    left: 0;
  }

  &[data-position='bottom-left'] {
    bottom: 0;
    left: 0;
    transform: scaleY(-1);
  }

  &[data-position='bottom-right'] {
    bottom: 0;
    right: 0;
    transform: scaleX(-1) rotate(-90deg);
  }
`;
const ImageWrapper = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  height: 82%;
  width: 100%;
  border-radius: 0 0 24px 24px;
  overflow: hidden;

  @media ${from(Device.TabletSmall)} {
    top: 0;
    right: 0;
    width: auto;
    max-width: 75%;
    height: 100%;
    border-radius: ${fluid(24, 48)} 0 0 ${fluid(24, 48)};

    ${Slide}[data-layout='split'] & {
      width: calc(50% - 33px);
      height: calc(100% - 80px);
      top: 50%;
      transform: translateY(-50%);
    }

    ${Slide}[data-layout='full-width'] & {
      border-radius: 0;
      width: 100%;
      max-width: none;
      height: 100%;
    }
  }

  @media ${until(Device.TabletSmall)} {
    ${Slide}[data-layout='wide'] & {
      top: unset;
      bottom: 0;
      height: 67.5%;
      border-radius: 24px 24px 0 0;
    }

    ${Slide}[data-layout='split'] & {
      top: unset;
      bottom: unset;
      position: relative;
      height: auto;
      margin-top: 32px;
      border-radius: 24px 24px 0 0;
    }

    ${Slide}[data-layout='full-width'] & {
      border-radius: 0;
      max-width: none;
      height: 100%;
    }
  }
`;
const Image = styled.img `
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
export const HeroHomepageBannerStyles = {
    Content,
    Cta,
    HeroHomepage,
    Image,
    ImageWrapper,
    Inner,
    RoundedCorner,
    Slide,
    Subtitle,
    Title,
};
